import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
// The JSON itself, not data/nav.ts: this module sits under data/venues.ts and
// must not pull in the nav → leagues → events → store chain.
import nav from '../data/nav.json';
import { isoDays, showtime, type Phase, type ShowEvent, type VenueZone } from '../../shared/showtime.mjs';

/** The day this build ran (nav.json `updated`, rewritten by every build), YYYY-MM-DD. */
export const BUILD_DAY: string = (nav as { updated: string }).updated;

/** The visitor's today, YYYY-MM-DD (UTC). */
export const realToday = () => new Date().toISOString().slice(0, 10);

/**
 * Today for date-dependent copy: the build day while rendering on the server and
 * hydrating (so both renders agree, even on the day the copy changes), the real
 * date right after mount — so dated copy turns over on time between builds too.
 */
export const useToday = () => {
  const [today, setToday] = useState(BUILD_DAY);
  useEffect(() => setToday(realToday()), []);
  return today;
};

/**
 * The visitor's IANA time zone for showing a moment ("Oct 3, 4:35 AM CEST"):
 * 'UTC' on the server and while hydrating — the zone is printed with the time,
 * so that is honest too — and the browser's own zone right after mount.
 */
export const useTimeZone = () => {
  const [zone, setZone] = useState('UTC');
  useEffect(() => {
    try {
      const z = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (z) setZone(z);
    } catch {
      /* keep UTC */
    }
  }, []);
  return zone;
};

// ---- the page clock --------------------------------------------------------------------------

/** One clock for the whole page: every subscriber flips at the same moment, one timer. */
let current: number | null = null;
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;
const tick = () => {
  current = Date.now();
  for (const fn of listeners) fn();
};
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  timer ??= setInterval(tick, 60_000);
  // The first subscriber after hydration starts the real clock (and a stale one is refreshed).
  if (current === null || Date.now() - current > 30_000) tick();
  return () => {
    listeners.delete(fn);
    if (!listeners.size && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
};

/**
 * Now (ms) for time-of-day copy — "Started at 8:00 PM", "checked 3 h ago". Null on the
 * server and during hydration (so both renders agree), then the real time right after
 * mount, refreshed every 60 s. It drives states, never a visible countdown.
 */
export const useNow = (): number | null =>
  useSyncExternalStore(
    subscribe,
    () => current,
    () => null
  );

export type ShowState = {
  /**
   * The venue-local phase (shared/showtime.mjs). Before mount it is judged by the build day
   * at date level only: 'today' or 'upcoming', never 'started' or 'ended' — and 'generic'
   * (the page as it always was) when the build day is already past the event's date.
   */
  phase: Phase | 'generic';
  /** calendar days to the event (venue-local after mount, from the build day before) */
  daysOut: number;
  /** the start instant, after mount and with a zone only */
  start: number | null;
  /** the zone is the venue's own (tz or offset), not a guess from its region */
  exact: boolean;
  zone: VenueZone | null;
  now: number | null;
};

/** Show-day state for an event: hydration-safe (see ShowState.phase). */
export const useShowTime = (e: ShowEvent | null | undefined): ShowState => {
  const now = useNow();
  const key = e ? [e.date, e.time ?? '', e.country ?? '', e.place?.tz ?? '', e.place?.offset ?? '', e.place?.region ?? '', e.kind ?? '', e.genre ?? '', e.category ?? '', e.name ?? ''].join('|') : '';
  return useMemo((): ShowState => {
    if (!e) return { phase: 'generic', daysOut: Number.NaN, start: null, exact: false, zone: null, now };
    if (now === null) {
      const d = isoDays(BUILD_DAY, e.date);
      return { phase: d >= 1 ? 'upcoming' : d === 0 ? 'today' : 'generic', daysOut: d, start: null, exact: false, zone: null, now: null };
    }
    const s = showtime(e, now);
    return { ...s, exact: !!s.zone && s.zone.kind !== 'region', now };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, now]);
};
